import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, MapPin, Phone, Clock, Package } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (e) {
        console.error("Error fetching order:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-unbounded text-2xl font-bold text-foreground mb-4">
            Order not found
          </h2>
          <Button asChild>
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-green-500" />
          </motion.div>
          <h1 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-4">
            Order Confirmed!
          </h1>
          <p className="font-outfit text-lg text-muted-foreground">
            Thank you for your order. We'll have it ready for pickup soon.
          </p>
        </motion.div>

        {/* Order Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl border border-border p-6 md:p-8 mb-8"
        >
          {/* Order ID */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-outfit text-sm text-muted-foreground">
                Order ID
              </p>
              <p
                className="font-unbounded font-semibold text-foreground"
                data-testid="order-id"
              >
                #{order.id.slice(0, 8).toUpperCase()}
              </p>
            </div>
            <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <span className="font-outfit text-sm font-medium text-primary capitalize">
                {order.status}
              </span>
            </div>
          </div>

          <Separator className="mb-6 bg-border" />

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="font-outfit text-sm text-muted-foreground mb-1">
                Name
              </p>
              <p className="font-outfit font-medium text-foreground">
                {order.customer_name}
              </p>
            </div>
            <div>
              <p className="font-outfit text-sm text-muted-foreground mb-1">
                Email
              </p>
              <p className="font-outfit font-medium text-foreground">
                {order.customer_email}
              </p>
            </div>
            <div>
              <p className="font-outfit text-sm text-muted-foreground mb-1">
                Phone
              </p>
              <p className="font-outfit font-medium text-foreground">
                {order.customer_phone}
              </p>
            </div>
            {order.pickup_time && (
              <div>
                <p className="font-outfit text-sm text-muted-foreground mb-1">
                  Preferred Pickup
                </p>
                <p className="font-outfit font-medium text-foreground">
                  {order.pickup_time}
                </p>
              </div>
            )}
          </div>

          {order.notes && (
            <>
              <div className="mb-6">
                <p className="font-outfit text-sm text-muted-foreground mb-1">
                  Order Notes
                </p>
                <p className="font-outfit text-foreground">{order.notes}</p>
              </div>
            </>
          )}

          <Separator className="mb-6 bg-border" />

          {/* Order Items */}
          <div className="mb-6">
            <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4">
              Order Items
            </h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div
                  key={`${item.product_id || 'item'}-${index}`}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                      <Package className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-outfit text-sm font-medium text-foreground">
                        {item.product_name}
                      </p>
                      <p className="font-outfit text-xs text-muted-foreground">
                        Qty: {item.quantity} × ${item.price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <span className="font-outfit font-medium text-foreground">
                    ${(item.quantity * item.price).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Separator className="mb-6 bg-border" />

          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="font-unbounded text-lg font-semibold text-foreground">
              Total
            </span>
            <span
              className="font-unbounded text-2xl font-bold text-primary"
              data-testid="order-total"
            >
              ${order.total.toFixed(2)}
            </span>
          </div>
        </motion.div>

        {/* Pickup Location Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card rounded-2xl border border-border p-6 md:p-8 mb-8"
        >
          <h3 className="font-unbounded text-lg font-semibold text-foreground mb-4">
            Pickup Location
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-outfit font-medium text-foreground">
                  Airo Vapor
                </p>
                <p className="font-outfit text-sm text-muted-foreground">
                  1823 E Santa Fe St
                  <br />
                  Gardner, KS 66030
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary" />
              <a
                href="tel:+19136051001"
                className="font-outfit text-muted-foreground hover:text-primary transition-colors"
              >
                (913) 605-1001
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <span className="font-outfit text-muted-foreground">
                Open Daily: 10 AM - 8 PM
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Button
              data-testid="get-directions-btn"
              asChild
              variant="outline"
              className="w-full border-border hover:bg-muted"
            >
              <a
                href="https://maps.app.goo.gl/7jYJikWFQLMz9qhK9"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Get Directions
              </a>
            </Button>
          </div>
        </motion.div>

        {/* Continue Shopping */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <Button
            data-testid="continue-shopping-btn"
            asChild
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold px-8 py-6 text-lg rounded-xl"
          >
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderConfirmation;
